import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import FindAHomeCenterStyles from './FindAHomeCenter.styled';
import Layout from '../../components/Layout/Layout';
import SEO from '../../components/SEO/SEO';
import Heading from '../../components/Heading/Heading';
import DealerSearch from '../../components/DealerSearch/DealerSearch';
import DealerResults from '../../components/DealerResults/DealerResults';
// TODO: Remove happy houses
// import SmilingSunIcon from '../../components/Shared/Icons/SmilingSun';
// import ParachuteHouseIcon from '../../components/Shared/Icons/ParachuteHouse';
import { getSuggestions, getGeocode } from '../../services/geocode';
import { getDealers } from '../../services/dealers';
import useGTM from '../../hooks/useGTM';
import theme from '../../styles/theme';

const zipcodeLength = 5;
const charactersBeforeSearch = 3;

function FindAHomeCenter({ className }) {
  const [triggerGTMEvent] = useGTM();
  const [dealers, setDealers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [geocode, setGeocode] = useState([]);
  const [lastSearch, setLastSearch] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [search, setSearch] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [error, setError] = useState(false);

  useEffect(() => {
    getGeocode()
      .then(data => {
        setGeocode(data.data);
        setIsLoading(false);

        if (
          data.data &&
          data.data?.cityStateFormatted &&
          data.data?.postalCode
        ) {
          setSuggestions([
            {
              label: data.data?.cityStateFormatted,
              value: data.data?.postalCode,
            },
          ]);
          setSearch(data.data?.postalCode);
          fetchDealers(data.data?.postalCode);
        }
      })
      .catch(console.error);
  }, []);

  const fetchDealers = (zipcode = search) => {
    if (zipcode !== lastSearch) {
      setDealers([]);
      setIsSearching(true);
      getDealers(zipcode)
        .then(data => {
          setIsSearching(false);
          setLastSearch(zipcode);
          if (data.data.length > 0) {
            setError(false);
            setDealers(data.data);
          } else {
            setError(
              "We couldn't find any retailers in your area, please try a different search."
            );
          }
        })
        .then(
          triggerGTMEvent({
            name: 'vmf.cta_click',
            location: 'Find a Home Center',
            category: 'home_decision',
            action: 'search',
            label: 'home_center_search',
          })
        )
        .catch(err => {
          setIsSearching(false);
          setError(err);
        });
    }
  };

  const fetchSuggestions = (_search = search) => {
    setIsSearching(true);
    getSuggestions(_search)
      .then(({ data, status }) => {
        const newValues = data.map(({ cityStateFormatted, postalCode }) => ({
          value: postalCode,
          label: cityStateFormatted,
        }));
        setError(false);
        setIsSearching(false);
        setSuggestions(newValues);
      })
      .catch(err => {
        setSuggestions([]);
        setIsSearching(false);
        setError(err);
      });
  };

  const handleChange = ({ value }) => {
    setSearch(value);

    if (value.length >= charactersBeforeSearch && isNaN(value)) {
      fetchSuggestions(value);
    } else if (value.length < charactersBeforeSearch) {
      setSuggestions([]);
    } else if (value.length === zipcodeLength && !isNaN(value)) {
      fetchDealers(value);
    }
  };

  const handleSubmit = e => {
    e?.stopPropagation();
    e?.preventDefault();

    if (
      search &&
      search.length === zipcodeLength &&
      !isNaN(search) &&
      search !== lastSearch
    ) {
      setDealers([]);
      fetchDealers();
    }
  };

  const handleBlur = e => {
    if (
      search &&
      search.length === zipcodeLength &&
      !isNaN(search) &&
      search !== lastSearch
    ) {
      fetchDealers();
    }
  };

  return (
    <Layout className={className} isLoading={isLoading}>
      <SEO
        title='Find A Home Center'
        description='Looking for a mobile or manufactured home? Come visit Vanderbilt Mortgage and Finance, Inc. and discover the closest home centers in your zip code.'
      />

      {!isLoading && (
        <Heading appearance='dark' background={theme.colors.blue10}>
          <h1>Start Searching for Your Dream Home </h1>
          <DealerSearch
            label='Enter zip code'
            search={search}
            suggestions={suggestions}
            touched={!!geocode?.postalCode}
            onBlur={handleBlur}
            onSubmit={handleSubmit}
            onChange={handleChange}
            isSearching={isSearching}
            error={
              !suggestions.length &&
              search.length >= charactersBeforeSearch &&
              isNaN(search)
                ? 'No matches found'
                : false
            }
          />
          {/* TODO: remove happy houses
          <div className="icons">
            <ParachuteHouseIcon />
            <SmilingSunIcon />
          </div> */}
        </Heading>
      )}

      <DealerResults results={dealers.slice(0, 8)} error={error} />
    </Layout>
  );
}

FindAHomeCenter.propTypes = {
  match: PropTypes.object,
  history: PropTypes.object,
  location: PropTypes.object,
  data: PropTypes.shape({}),
  error: PropTypes.object,
};

export default FindAHomeCenterStyles(FindAHomeCenter);
